import { inject, injectable } from "@launchtray/tsyringe-async";
import { ModuleFlowExecutor } from "by/gamefactory/cityversetycoon/composition/module/ModuleFlowExecutor";
import { SceneManager } from "by/gamefactory/scene/SceneManager";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { TopPanel } from "../../game/components/toppanel/TopPanel";
import { UserProfileModule } from "./UserProfileModule";
import { IPlayerModel } from "../../common/models/IPlayerModel";
import { UserProfilePopupScene, UserProfileSceneOptions } from "./profile/UserProfilePopupScene";
import { UserProfileResult } from "./profile/UserProfileResult";
import { IUserLevelUpModel } from "../userlevelup/model/IUserLevelUpModel";

type UserProfilePopupSceneFactory = (options: UserProfileSceneOptions) => Promise<UserProfilePopupScene>;

@injectable()
export class UserProfileModuleFlowExecutor extends ModuleFlowExecutor<UserProfileModule, UserProfileResult> {
    private readonly sceneManager: SceneManager;
    private readonly logger: ILogger;
    private readonly playerModel: IPlayerModel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly topPanel: TopPanel;

    private topPanelOnNotificationAfterExecute: boolean;

    public constructor(
        sceneManager: SceneManager,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger") logger: ILogger,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        topPanel: TopPanel
    ) {
        super();
        this.sceneManager = sceneManager;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;
        this.playerModel = playerModel;
        this.topPanel = topPanel;
        this.topPanel.onLayerChanged.add(this.onTopPanelLayerChanged, this);
    }

    public override async execute(module: UserProfileModule, userLevelModel?: IUserLevelUpModel): Promise<UserProfileResult> {
        try {
            this.asyncOperationNotification.show();
            const userProfileScene: UserProfilePopupSceneFactory = await module.resolve("UserProfileSceneFactory");
            const sceneOptions: UserProfileSceneOptions = {
                userLevelUpModel: undefined
            };
            if (userLevelModel != null) {
                sceneOptions.userLevelUpModel = userLevelModel
            }
            if (sceneOptions.userLevelUpModel == null) {
                await this.playerModel.loadPlayerData();
            }
            const scene = await userProfileScene(sceneOptions);
            this.asyncOperationNotification.hide();
            this.topPanelOnNotificationAfterExecute = this.topPanel.onNotificationLayer;
            if (this.topPanelOnNotificationAfterExecute) {
                this.topPanel.bringToOriginParent(true);
            }
            const sceneResult = await this.sceneManager.showViewableAsync(scene);
            this.topPanel.onLayerChanged.remove(this.onTopPanelLayerChanged, this);
            if (this.topPanelOnNotificationAfterExecute) {
                this.topPanel.bringToFront();
            }
            else {
                this.topPanel.bringToOriginParent();
            }
            return sceneResult;
        }
        catch (e: unknown) {
            this.logger.error(`Exception on executing MyWalletFlow: `, e);
            this.asyncOperationNotification.hide();
            return UserProfileResult.NONE;
        }
        finally {
            this.topPanel.onLayerChanged.remove(this.onTopPanelLayerChanged, this);
        }
    }

    private onTopPanelLayerChanged(): void {
        this.topPanelOnNotificationAfterExecute = this.topPanel.onNotificationLayer;
        if (this.topPanel.onNotificationLayer) {
            this.topPanel.bringToOriginParent(true);
        }
    }
}
